import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  NgbDate,
  NgbCalendar,
  NgbDatepickerConfig,
} from '@ng-bootstrap/ng-bootstrap';

export interface DateRange {
  start: string;
  end: string;
}

@Component({
  selector: 'app-filter-date-range',
  templateUrl: './filter-date-range.component.html',
  styleUrls: ['./filter-date-range.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilterDateRangeComponent),
      multi: true,
    },
  ],
})
export class FilterDateRangeComponent implements ControlValueAccessor {
  @Input() label = 'Fecha';
  @Output() rangeChange = new EventEmitter<DateRange>();

  fromDate: NgbDate | null = null;
  toDate: NgbDate | null = null;
  hoveredDate: NgbDate | null = null;
  isOpen = false;

  private onChange: (value: DateRange | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private calendar: NgbCalendar,
    private config: NgbDatepickerConfig
  ) {
    this.config.firstDayOfWeek = 1;
  }

  // ControlValueAccessor
  writeValue(value: DateRange | null): void {
    if (value) {
      this.fromDate = this.parseDate(value.start);
      this.toDate = this.parseDate(value.end);
    } else {
      this.fromDate = null;
      this.toDate = null;
    }
  }

  registerOnChange(fn: (value: DateRange | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Date selection
  onDateSelection(date: NgbDate): void {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && !date.before(this.fromDate)) {
      this.toDate = date;
      this.emitValue();
      this.isOpen = false;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
  }

  isHovered(date: NgbDate): boolean {
    return (
      this.fromDate !== null &&
      this.toDate === null &&
      this.hoveredDate !== null &&
      !date.before(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate): boolean {
    return (
      this.toDate !== null &&
      !date.before(this.fromDate) &&
      !date.after(this.toDate)
    );
  }

  isRange(date: NgbDate): boolean {
    return (
      date.equals(this.fromDate) ||
      (this.toDate !== null && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  // Quick filters
  setToday(): void {
    const today = this.calendar.getToday();
    this.fromDate = today;
    this.toDate = today;
    this.emitValue();
    this.isOpen = false;
  }

  setThisWeek(): void {
    const today = this.calendar.getToday();
    const jsDate = new Date(today.year, today.month - 1, today.day);
    const dayOfWeek = jsDate.getDay();
    const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const monday = new Date(jsDate);
    monday.setDate(jsDate.getDate() - diffToMonday);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    this.fromDate = new NgbDate(
      monday.getFullYear(),
      monday.getMonth() + 1,
      monday.getDate()
    );
    this.toDate = new NgbDate(
      sunday.getFullYear(),
      sunday.getMonth() + 1,
      sunday.getDate()
    );
    this.emitValue();
    this.isOpen = false;
  }

  setThisMonth(): void {
    const today = this.calendar.getToday();
    const lastDay = new Date(today.year, today.month, 0).getDate();
    this.fromDate = new NgbDate(today.year, today.month, 1);
    this.toDate = new NgbDate(today.year, today.month, lastDay);
    this.emitValue();
    this.isOpen = false;
  }

  setLastMonth(): void {
    const today = this.calendar.getToday();
    const lastMonth = today.month === 1 ? 12 : today.month - 1;
    const lastMonthYear = today.month === 1 ? today.year - 1 : today.year;
    const lastDayOfLastMonth = new Date(lastMonthYear, lastMonth, 0).getDate();

    this.fromDate = new NgbDate(lastMonthYear, lastMonth, 1);
    this.toDate = new NgbDate(lastMonthYear, lastMonth, lastDayOfLastMonth);
    this.emitValue();
    this.isOpen = false;
  }

  // UI helpers
  togglePicker(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.onTouched();
    }
  }

  closePicker(): void {
    this.isOpen = false;
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.fromDate = null;
    this.toDate = null;
    this.onChange(null);
    this.rangeChange.emit({ start: '', end: '' });
  }

  formatDateRange(): string {
    if (!this.fromDate) return 'Seleccionar fechas';
    if (!this.toDate) return `${this.formatDate(this.fromDate)} - ...`;
    return `${this.formatDate(this.fromDate)} - ${this.formatDate(
      this.toDate
    )}`;
  }

  get hasValue(): boolean {
    return this.fromDate !== null && this.toDate !== null;
  }

  private formatDate(date: NgbDate): string {
    const day = date.day.toString().padStart(2, '0');
    const month = date.month.toString().padStart(2, '0');
    return `${day}/${month}/${date.year}`;
  }

  private toISODate(date: NgbDate): string {
    const month = date.month.toString().padStart(2, '0');
    const day = date.day.toString().padStart(2, '0');
    return `${date.year}-${month}-${day}`;
  }

  private parseDate(dateStr: string): NgbDate | null {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    return new NgbDate(
      parseInt(parts[0], 10),
      parseInt(parts[1], 10),
      parseInt(parts[2], 10)
    );
  }

  private emitValue(): void {
    if (this.fromDate && this.toDate) {
      const value: DateRange = {
        start: this.toISODate(this.fromDate),
        end: this.toISODate(this.toDate),
      };
      this.onChange(value);
      this.rangeChange.emit(value);
    }
  }
}
